import { Globe, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';

/**
 * Internal environment switcher — jumps between the local / dev / prod
 * frontends, preserving the current path (e.g. dev `/orders/5` → prod
 * `/orders/5`). It only navigates; it never repoints a backend or touches auth.
 *
 * Gated: renders ONLY when the signed-in email is allowlisted OR a localStorage
 * override key is set (so it's automatic for the owner but force-enable-able on
 * any browser). The gate is purely cosmetic — these are public frontend URLs —
 * so a client-side check is sufficient.
 */
const ENVS = [
  { key: 'local', label: 'Local', origin: 'http://localhost:5173' },
  {
    key: 'dev',
    label: 'Dev',
    origin: 'https://wms-dev.modularclosets-apps.com',
  },
  { key: 'prod', label: 'Prod', origin: 'https://wms.modularclosets-apps.com' },
] as const;

const ALLOWED_EMAILS = [
  'yitzn@modularclosets.com',
  'davidp@modularclosets.com',
];
const OVERRIDE_KEY = 'wms-env-switcher';

// Accept any reasonable truthy value for the localStorage override so you don't
// have to remember an exact magic string — 'true', '1', 'yes', 'on' all work.
function overrideEnabled(): boolean {
  try {
    const v = (localStorage.getItem(OVERRIDE_KEY) || '').trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes' || v === 'on';
  } catch {
    return false;
  }
}

// Which hosted origin am I viewing? (hostname-based, on purpose — this drives
// navigation between origins, a different question from the build-time env that
// colors the header. Returns null on unknown hosts e.g. a dev tunnel.)
function currentEnvKey(): (typeof ENVS)[number]['key'] | null {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1') return 'local';
  if (h === 'wms-dev.modularclosets-apps.com') return 'dev';
  if (h === 'wms.modularclosets-apps.com') return 'prod';
  return null;
}

export function EnvSwitcher({ className }: { className?: string }) {
  const email = useAuthStore((s) => s.user?.email);
  const enabled =
    (!!email && ALLOWED_EMAILS.includes(email)) || overrideEnabled();

  if (!enabled) return null;

  const current = currentEnvKey();
  const currentLabel = ENVS.find((e) => e.key === current)?.label ?? 'Env';

  const go = (origin: string) => {
    const target = origin.replace(/\/$/, '');
    if (target === window.location.origin.replace(/\/$/, '')) return;
    // Preserve the full path so you land on the same page in the other env.
    window.location.assign(
      target +
        window.location.pathname +
        window.location.search +
        window.location.hash,
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn('gap-1.5 px-2', className)}
          title="Switch environment (keeps current page)"
        >
          <Globe size={16} />
          <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wide">
            {currentLabel}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs">
          Open this page on…
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ENVS.map((e) => (
          <DropdownMenuItem
            key={e.key}
            onClick={() => go(e.origin)}
            disabled={e.key === current}
            className="flex items-center justify-between"
          >
            <span>{e.label}</span>
            {e.key === current && <Check size={14} weight="bold" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
